import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import Icon from "@material-ui/core/Icon";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DataTable, { Pill, formatUGX } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";

const styles = {
  heroWrap: {
    position: "relative",
    width: "100%",
    height: "240px",
    borderRadius: "20px",
    overflow: "hidden",
    marginBottom: "30px",
    background: "linear-gradient(160deg, #1a237e 0%, #283593 60%, #3949ab 100%)",
    boxShadow: "0 20px 40px -15px rgba(26, 35, 126, 0.3)"
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%)",
    zIndex: 1
  },
  heroContent: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    padding: "40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    color: "#fff"
  },
  heroTitle: { fontSize: "32px", fontWeight: 800, margin: 0, letterSpacing: "-0.5px" },
  heroSub: { fontSize: "15px", margin: "8px 0 0", opacity: 0.9, fontWeight: 400 },
  heroActions: { position: "absolute", top: "40px", right: "40px", zIndex: 3, display: "flex", gap: "12px" },

  // KPI stat cards
  kpiRow: { display: "flex", gap: "16px", marginBottom: "32px", flexWrap: "wrap" },
  statCard: {
    flex: 1,
    minWidth: "220px",
    padding: "20px",
    borderRadius: "16px",
    background: "#fff",
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    transition: "transform 0.2s ease",
    border: "1px solid #f1f5f9",
    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 30px 0 rgba(0,0,0,0.08)" }
  },
  iconBox: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  statNumber: { fontSize: "18px", fontWeight: 800, color: "#1e293b", margin: 0 },
  statLabel: { fontSize: "11px", color: "#64748b", margin: "2px 0 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" },
  statSub: { fontSize: "11px", color: "#94a3b8", margin: "2px 0 0" },

  // Toolbar
  toolbarWrap: {
    background: "#fff",
    padding: "16px 24px",
    borderRadius: "16px 16px 0 0",
    borderBottom: "1px solid #f1f5f9",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px"
  },
  filterGroup: { display: "flex", alignItems: "center", gap: "12px" },
  filterSelect: { 
    padding: "8px 12px", 
    borderRadius: "8px", 
    border: "1px solid #e2e8f0", 
    fontSize: "13px", 
    color: "#475569",
    outline: "none",
    "&:focus": { borderColor: "#3f51b5" }
  },

  tableContainer: {
    background: "#fff",
    borderRadius: "0 0 16px 16px",
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
    overflow: "hidden"
  },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px", marginBottom: "10px" }
};

const CATEGORIES = ["Tuition", "Salaries", "Infrastructure", "Administration", "Library", "Examination", "ICT", "Catering", "Maintenance", "Other"];
const PERIODS = ["Semester 1", "Semester 2", "Full Year"];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map(String);

const BLANK = { description: "", category: "", allocatedAmount: "", period: "Full Year", academicYear: String(CURRENT_YEAR) };

class BudgetDetails extends Component {
  state = {
    budgetLines: [],
    expenditures: [],
    loading: true,
    yearFilter: String(CURRENT_YEAR),
    categoryFilter: "",
    open: false,
    editing: null,
    form: { ...BLANK },
    saving: false,
    error: null
  };

  componentDidMount() { this.fetchAll(); }

  fetchAll = () => {
    this.fetchBudgetLines();
    this.fetchExpenditures();
  };

  fetchBudgetLines = async () => {
    this.setState({ loading: true });
    const { yearFilter, categoryFilter } = this.state;
    try {
      const params = { limit: 200, year: yearFilter };
      if (categoryFilter) params.category = categoryFilter;
      const res = await api.get("/finance/budget", { params });
      const d = res.data.data || res.data;
      this.setState({ budgetLines: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) {
      this.setState({ loading: false });
    }
  };

  fetchExpenditures = async () => {
    const { yearFilter } = this.state;
    try {
      const res = await api.get("/finance/expenditure", { params: { limit: 500, year: yearFilter } });
      const d = res.data.data || res.data;
      this.setState({ expenditures: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* silent */ }
  };

  handleChange = field => e => {
    const v = e.target.value;
    this.setState(prev => ({ form: { ...prev.form, [field]: v } }));
  };

  openAdd = () => {
    this.setState({
      open: true, editing: null, error: null,
      form: { ...BLANK, academicYear: this.state.yearFilter }
    });
  };

  openEdit = line => {
    this.setState({
      open: true, editing: line.id, error: null,
      form: {
        description: line.description || "",
        category: line.category || "",
        allocatedAmount: line.allocatedAmount !== undefined ? String(line.allocatedAmount) : String(line.amount || ""),
        period: line.period || "Full Year",
        academicYear: line.academicYear || line.year || this.state.yearFilter
      }
    });
  };

  handleSave = async () => {
    const { form, editing } = this.state;
    if (!form.category || !form.allocatedAmount) {
      this.setState({ error: "Category and allocated amount are required." });
      return;
    }
    this.setState({ saving: true, error: null });
    try {
      const payload = {
        ...form,
        allocatedAmount: parseFloat(form.allocatedAmount),
        amount: parseFloat(form.allocatedAmount),
        year: form.academicYear
      };
      if (editing) {
        await api.put(`/finance/budget/${editing}`, payload);
      } else {
        await api.post("/finance/budget", payload);
      }
      this.setState({ open: false, saving: false });
      this.fetchAll();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed to save.", saving: false });
    }
  };

  getSpentForLine = lineId => {
    return this.state.expenditures
      .filter(e => e.budgetLineId === lineId)
      .reduce((sum, e) => sum + (e.amount || 0), 0);
  };

  render() {
    const { classes } = this.props;
    const { budgetLines, loading, yearFilter, categoryFilter, open, editing, form, saving, error } = this.state;

    const totalAllocated = budgetLines.reduce((s, b) => s + (b.allocatedAmount || b.amount || 0), 0);
    const totalSpent = budgetLines.reduce((s, b) => s + this.getSpentForLine(b.id), 0);
    const balance = totalAllocated - totalSpent;

    const pctSpent = totalAllocated ? Math.min(Math.round((totalSpent / totalAllocated) * 100), 100) : 0;
    const isOver   = balance < 0;

    const budgetRows = budgetLines.map(b => {
      const allocated = b.allocatedAmount || b.amount || 0;
      const spent = this.getSpentForLine(b.id);
      const balance = allocated - spent;
      const over = spent > allocated;
      const status = over ? "Over Budget" : pctSpent >= 80 ? "Critical" : "On Track";
      const statusTone = over ? "danger" : pctSpent >= 80 ? "warning" : "success";
      return { ...b, allocated, spent, balance, over, status, statusTone };
    });

    const columns = [
      {
        id: "lineItem",
        header: "Line Item",
        accessorFn: b => b.description || "—",
        cell: info => <span style={{ fontWeight: 600, color: "#1e293b" }}>{info.getValue()}</span>
      },
      {
        id: "category",
        header: "Category",
        accessorFn: b => b.category || "Other",
        cell: info => <Pill tone="muted">{info.getValue()}</Pill>
      },
      {
        id: "allocated",
        header: "Allocated (UGX)",
        accessorFn: b => b.allocated,
        meta: { align: "right" },
        cell: info => <span style={{ fontWeight: 600 }}>{formatUGX(info.getValue())}</span>
      },
      {
        id: "spent",
        header: "Spent (UGX)",
        accessorFn: b => b.spent,
        meta: { align: "right" },
        cell: info => (
          <span style={{ color: info.row.original.over ? "#ef4444" : "#475569", fontWeight: info.row.original.over ? 700 : 500 }}>
            {formatUGX(info.getValue())}
          </span>
        )
      },
      {
        id: "balance",
        header: "Balance (UGX)",
        accessorFn: b => b.balance,
        meta: { align: "right" },
        cell: info => {
          const over = info.row.original.over;
          const v = info.getValue();
          return (
            <span style={{ color: over ? "#ef4444" : "#10b981", fontWeight: 700 }}>
              {over ? `-${formatUGX(Math.abs(v))}` : formatUGX(v)}
            </span>
          );
        }
      },
      {
        id: "period",
        header: "Period",
        accessorFn: b => b.period || "—",
        cell: info => <span style={{ fontSize: "13px", color: "#64748b" }}>{info.getValue()}</span>
      },
      {
        id: "status",
        header: "Status",
        cell: info => <Pill tone={info.row.original.statusTone}>{info.row.original.status}</Pill>
      },
      {
        id: "actions",
        header: "",
        cell: info => (
          <Button size="sm" color="info" justIcon simple onClick={() => this.openEdit(info.row.original)}>
            <Icon>edit</Icon>
          </Button>
        )
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          {/* Hero Section */}
          <div className={classes.heroWrap}>
            <div className={classes.heroOverlay} />
            <div className={classes.heroContent}>
              <h4 className={classes.heroTitle}>Institutional Budget</h4>
              <p className={classes.heroSub}>
                Comprehensive financial planning and expenditure tracking for {yearFilter}
              </p>
            </div>
            <div className={classes.heroActions}>
              <FormControl style={{ minWidth: "120px" }}>
                <Select
                  value={yearFilter}
                  onChange={e => this.setState({ yearFilter: e.target.value }, this.fetchAll)}
                  style={{ color: "#fff", fontSize: "14px", fontWeight: 600 }}
                  disableUnderline
                >
                  {YEARS.map(y => <MenuItem key={y} value={y}>{y} Academic Year</MenuItem>)}
                </Select>
              </FormControl>
              <Button color="white" round onClick={this.openAdd}>
                <Icon>add_circle</Icon> New Budget Line
              </Button>
            </div>
          </div>

          {/* KPI Stat Cards */}
          <div className={classes.kpiRow}>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#e0e7ff", color: "#4338ca" }}>
                <Icon>account_balance_wallet</Icon>
              </div>
              <div>
                <p className={classes.statLabel}>Total Allocated</p>
                <h3 className={classes.statNumber}>UGX {totalAllocated.toLocaleString()}</h3>
                <p className={classes.statSub}>{budgetLines.length} Budget Lines</p>
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: isOver ? "#fef2f2" : "#f0fdf4", color: isOver ? "#ef4444" : "#10b981" }}>
                <Icon>{isOver ? "warning" : "trending_up"}</Icon>
              </div>
              <div>
                <p className={classes.statLabel}>Total Spent</p>
                <h3 className={classes.statNumber}>UGX {totalSpent.toLocaleString()}</h3>
                <p className={classes.statSub}>{pctSpent}% Utilization</p>
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#fffbeb", color: "#d97706" }}>
                <Icon>pie_chart</Icon>
              </div>
              <div>
                <p className={classes.statLabel}>Remaining Balance</p>
                <h3 className={classes.statNumber}>UGX {Math.max(0, balance).toLocaleString()}</h3>
                <p className={classes.statSub}>{isOver ? "Budget Exceeded" : "Available Funds"}</p>
              </div>
            </div>
          </div>

          {/* Table Card */}
          <div className={classes.tableContainer}>
            <div className={classes.toolbarWrap}>
              <div className={classes.filterGroup}>
                <Icon style={{ color: "#94a3b8" }}>filter_list</Icon>
                <select 
                  className={classes.filterSelect} 
                  value={categoryFilter} 
                  onChange={e => this.setState({ categoryFilter: e.target.value }, this.fetchBudgetLines)}
                >
                  <option value="">All Categories</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ fontSize: "13px", fontWeight: 600, color: "#64748b" }}>
                Showing {budgetLines.length} budget lines
              </div>
            </div>

            <DataTable
              columns={columns}
              data={budgetRows}
              isLoading={loading}
              emptyState={
                <div>
                  <Icon style={{ fontSize: "64px", color: "#cbd5e1" }}>account_balance</Icon>
                  <h4 style={{ margin: "16px 0 8px", color: "#1e293b", fontWeight: 700 }}>No Budget Lines Found</h4>
                  <p style={{ color: "#64748b", maxWidth: "400px", margin: "0 auto" }}>
                    There are no budget lines recorded for the {yearFilter} academic year.
                    Click "New Budget Line" to start planning.
                  </p>
                </div>
              }
            />
          </div>
        </GridItem>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title={editing ? "Edit Budget Line" : "Add Budget Line"} />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <CustomInput labelText="Description" id="bdDesc" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.description, onChange: this.handleChange("description") }} />
            <FormControl className={classes.formControl}>
              <InputLabel>Category *</InputLabel>
              <Select value={form.category} onChange={this.handleChange("category")}>
                <MenuItem value="">— Select —</MenuItem>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <CustomInput labelText="Allocated Amount (UGX) *" id="bdAmount" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.allocatedAmount, onChange: this.handleChange("allocatedAmount"), type: "number", min: "0" }} />
            <FormControl className={classes.formControl}>
              <InputLabel>Period</InputLabel>
              <Select value={form.period} onChange={this.handleChange("period")}>
                {PERIODS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl}>
              <InputLabel>Academic Year</InputLabel>
              <Select value={form.academicYear} onChange={this.handleChange("academicYear")}>
                {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : editing ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

BudgetDetails.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(BudgetDetails));
